import { SortParams } from 'models';

import { isValid as isDateValid, startOfDay } from 'date-fns';

function formatToDate(d: string) {
  let [dd, mm, yyyy] = d.split('.');

  return startOfDay(
    new Date(`${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`)
  );
}

function isValidTiming(d: string) {
  let regex = /^(0?[1-9]|[12][0-9]|3[01])[.](0?[1-9]|1[012])[.]\d{4}$/;

  return regex.test(d);
}

type EstimatedTimingEntity = {
  EstimatedTiming: string | null;
};

export function compareFn<T extends EstimatedTimingEntity>(a: T, b: T) {
  if (a.EstimatedTiming && b.EstimatedTiming) {
    const [aStart, aEnd] = a.EstimatedTiming.split('-');
    const [bStart, bEnd] = b.EstimatedTiming.split('-');

    let aStartValid = Boolean(aStart && isValidTiming(aStart.trim())),
      aEndValid = Boolean(aEnd && isValidTiming(aEnd.trim())),
      bStartValid = Boolean(bStart && isValidTiming(bStart.trim())),
      bEndValid = Boolean(bEnd && isValidTiming(bEnd.trim()));

    if (aStartValid && bStartValid) {
      let aStartDate = formatToDate(aStart.trim());
      let bStartDate = formatToDate(bStart.trim());
      let aStartDateValid = isDateValid(aStartDate);
      let bStartDateValid = isDateValid(bStartDate);

      if (aStartDateValid && bStartDateValid) {
        let aStartDateTime = aStartDate.getTime();
        let bStartDateTime = bStartDate.getTime();

        if (aStartDateTime > bStartDateTime) {
          return 1;
        }

        if (aStartDateTime < bStartDateTime) {
          return -1;
        }

        if (aEndValid && bEndValid) {
          let aEndDate = formatToDate(aEnd.trim());
          let bEndDate = formatToDate(bEnd.trim());
          let aEndDateValid = isDateValid(aEndDate);
          let bEndDateValid = isDateValid(bEndDate);

          if (aEndDateValid && bEndDateValid) {
            let aEndDateTime = aEndDate.getTime();
            let bEndDateTime = bEndDate.getTime();

            if (aEndDateTime > bEndDateTime) {
              return 1;
            }

            if (aEndDateTime < bEndDateTime) {
              return -1;
            }

            return 0;
          }
        }

        if (!aEndValid && bEndValid) {
          return -1;
        }

        if (aEndValid && !bEndValid) {
          return 1;
        }
      }
    }

    if (!aStartValid && bStartValid) {
      return 1;
    }

    if (aStartValid && !bStartValid) {
      return -1;
    }

    if (!aStartValid && !bStartValid) {
      if (a.EstimatedTiming > b.EstimatedTiming) {
        return 1;
      }

      if (a.EstimatedTiming < b.EstimatedTiming) {
        return -1;
      }

      return 0;
    }
  }

  if (a.EstimatedTiming && !b.EstimatedTiming) {
    return -1;
  }

  if (!a.EstimatedTiming && b.EstimatedTiming) {
    return 1;
  }

  return 0;
}

export default function sortByEstimatedTiming<T extends EstimatedTimingEntity>(
  list: T[],
  sortParams: SortParams
): T[] {
  let order = sortParams.order === 'asc' ? -1 : 1;

  return [...list].sort((a, b) => {
    const orderNum = compareFn(a, b);

    return orderNum * order;
  });
}
